import { useMemo, useState, useRef } from "react";
import moment from "moment";
import { useAppTranslation } from "../../../../../../../../i18n/useAppTranslation";
import { uiNotification } from "../../../../../../../../services/UINotificationService";
import { useSalesExpiredLiabilityReportQuery } from "../../../../../../../../api/queries/useSalesExpiredLiabilityReportQuery";

const prepareReqPeriod = (periodState) => {
  return {
    from: periodState.from || null,
    to: periodState.to || null,
  };
};

export function useReports() {
  const { tCommon } = useAppTranslation.Common();

  const [period, setPeriod] = useState({
    from: moment().startOf("month").toDate(),
    to: moment().endOf("month").toDate(),
  });

  const [clinicId, setClinicId] = useState(null);
  const accumulatedClinicsRef = useRef(new Map());

  const {
    data,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useSalesExpiredLiabilityReportQuery({
    payload: {
      period: prepareReqPeriod(period),
      pageSize: 15,
      clinicId: clinicId,
    },
    options: {
      staleTime: 0,
      cacheTime: 0,
      onError: () => {
        uiNotification.error(tCommon("error.getReports"));
      },
    },
  });

  const isPeriodActive = Object.values(period).some(Boolean);

  const isSoftLoading =
    isPeriodActive && isFetching && !isLoading && !isFetchingNextPage;

  const reports = useMemo(() => {
    if (data?.pages) {
      return data.pages
        .map((p) => p.reports)
        .reduce((r, p) => [...r, ...p], []);
    }
    return [];
  }, [data?.pages]);

  const allClinics = useMemo(() => {
    if (reports && reports.length > 0) {
      reports.forEach((report) => {
        if (report.clinicId && report.clinicName) {
          accumulatedClinicsRef.current.set(report.clinicId, report.clinicName);
        }
      });
    }

    return Array.from(accumulatedClinicsRef.current.entries()).map(
      ([id, name]) => ({
        id,
        name,
      }),
    );
  }, [reports]);

  return {
    reports: {
      total: data?.pages?.[0]?.stats?.total,
      value: reports,
      isLoading,
      isSoftLoading,
      hasMore: Boolean(hasNextPage),
      fetchMore: fetchNextPage,
      isNextFetching: isFetchingNextPage,
    },
    period: {
      value: period,
      update: setPeriod,
    },
    clinic: {
      value: clinicId,
      onChange: setClinicId,
      options: allClinics,
    },
  };
}
